/**
 * 上传文件组件
 * 使用方：用户对话，群组对话
 * @author sunzhiguang
 * @date 2020/6/10
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import "./index.less";
import IconFont from "@/components/icon";
import { Tooltip, message as toast, Dropdown } from "antd";
import { useTranslation, WithTranslation } from "react-i18next";
import {
  ChatMessage,
  ChatMessageType,
  Employee,
  FileChatMessage,
  FileStatus,
  MessageStatus,
  SessionType
} from "@/types/chat";
import {
  downLoadFile,
  getFileIconByMime,
  getFileName,
  isImageFileName,
  getFileUrl,
  sizeToStr,
  getFileFilter,
  getDownloadFileResp,
  getPreviewCanEdit
} from "@/utils/chat/index";
import { MessageSendType } from "@/types/chat/enum";
import ChatState from "@/types/chat/State";
import { connect } from "dva";
import UploadControl from "@/components/chat/message/MsgControl/UploadControl";
import { detectOS, FocusSDK, isFocusEnv, DownloadItem } from "@/utils";
import EventBus from "@/utils/bus";
import {
  checkFileIsExpired,
  viewUploadFile,
  getPreviewImageModal
} from "@/components/chat/utils/commonMessageModal";
import ConnectState from "@jd/jdee.im.sdk/lib/es/enum/ConnectState";
import ImService from "@/server/ImService";
import {
  changeEgovUrl,
  crossAppSwitch,
  getFileOperate,
  getFileOperateAuthority,
  getFileTagById,
  getFileTagName
} from "@/utils/tools";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import NetworkCheck, { UseCheckType } from "@/utils/NetworkCheck";
import { UserModel } from "@/models/user";
import { handleTransferTo } from "@/components/chat/message/Item/utils";
import ChatContext from "@/components/chat/context";
import { getNetCheckUrl } from "@/config/config";
import { getGroupMemberList, getWpsPreviewCode } from "@/api/chat";
import i18n from "@/i18n";
import { groupCreates } from "@/components/Joyspace/api";
import { addXclient } from "@/utils/addToken";
import egovFileIcon from "@/assets/images/im/egov-file-icon.png";
import {
  isCrossPlatformGroupSession,
  isEqualEmployee,
  isOuterContacts,
  parseMessageToUserBySingle,
  showIntentTail,
  getFileDownloadBzOptions
} from "@/utils/chat/message";
import { canJoyspacePreview, openJoyspacePreview } from "@/components/Joyspace/util";
import { DownOutlined } from "@/utils/antdIcon";
import UploadWithDetailBtn from "./UploadWithDetailBtn";
import { FileTipTag } from "@/components/Tags";
import AIIntentTail from "../../AIIntentTail";
import { pick } from "lodash";
import MessageDongReadStatusBtn from "@/components/chat/message/Item/MessageDongReadStatusBtn";
interface UploadFileProps {
  isMergeForward?: boolean;
  message: FileChatMessage;
  sendType: MessageSendType;
  sessionType: SessionType;
  currentEmployee: Employee;
  onReady: (params: { beginSend: boolean; messageExtend: any }) => {};
  clearTempMessage: Function;
  userData: UserModel;
}

interface DvaDispatchProps {
  updateSessionMessageMid: Function;
  pushChatMessageMid: (data: { sessionId: string; message: Partial<ChatMessage> }) => void;
}

type IProps = Readonly<WithTranslation & UploadFileProps & DvaDispatchProps & ChatState>;
let dXhr: any = {};
// eslint-disable-next-line complexity
function UploadFile(props: IProps) {
  const {
    isMergeForward,
    message,
    messageUploadStateMap,
    connectState,
    onReady,
    selectedSession,
    currentEmployee,
    clearTempMessage,
    userData,
    pushChatMessageMid,
    updateSessionMessageMid
  } = props;
  const { appRuntimeEnv, localConfigData } = useContext<GlobalContextConfig>(GlobalContext);
  const {
    joyspaceHost,
    im,
    fileService: { imAppKey },
    joyspaceWpsTarget
  } = appRuntimeEnv;
  const defaultDownloadPath =
    appRuntimeEnv.registryConfig.defaultDownloadPath || FocusSDK.getDownloadPath();
  const messageUploadState = useMemo(() => {
    return messageUploadStateMap[message.id] || null;
  }, [message, messageUploadStateMap]);

  const isEgovFile = useMemo(() => {
    return message.type === ChatMessageType.EGOV_FILE || message.kind === ChatMessageType.EGOV_FILE;
  }, [message]);

  const { t } = useTranslation("chat");
  const [status, setStatus] = useState(
    isFocusEnv() ? FileStatus.INITING : FileStatus.WEB_COMPLETED
  );
  const [progress, setProgress] = useState(0.1);
  const [beginSend, setBeginSend] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadResult, setUploadResult] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [path, setPath] = useState("");
  const [isWpsFile, setIsWpsFile] = useState(false);
  const { groupRosters, isMeetingChat, fileTagList, currentFileControl } = React.useContext(
    ChatContext
  );
  const isOutApp = selectedSession.isGroup
    ? isCrossPlatformGroupSession(selectedSession)
    : isOuterContacts(selectedSession, currentEmployee);
  const isDisplay = !isOutApp && crossAppSwitch("joyspace");

  const task = useMemo(() => {
    if (message.taskId && message.task) {
      return message.task;
    }
    return null;
  }, [message]);

  const isImageFile = useMemo(() => {
    const isImg = isImageFileName(getFileName(message));
    return isImg;
  }, [message]);

  const canJoyspaceEdit = useMemo(() => {
    const auth = getFileOperateAuthority("tranferTo", currentFileControl || [], message?.fileTag || 0);
    return auth ? 1 : 0;
  }, [currentFileControl, message.fileTag]);

  const hasAuthPreview = useMemo(() => {
    const auth = getFileOperateAuthority(
      "onlinePreview",
      currentFileControl || [],
      message?.fileTag || 0
    );
    return auth;
  }, [currentFileControl, message.fileTag]);

  const canWpsPreview = useMemo(() => {
    return hasAuthPreview && isDisplay && !selectedSession.isSecret && joyspaceWpsTarget === 'private';
  }, [hasAuthPreview, isDisplay, selectedSession.isSecret]);

  useEffect(() => {
    if (message.name) {
      const res = canJoyspacePreview(message.name);
      setIsWpsFile(res);
    }
  }, [message?.name]);

  useEffect(() => {
    if (isFocusEnv() && message.status !== MessageStatus.INIT) {
      const upLocalPath = message.upLocalPath;
      FocusSDK.checkLocalPathExist(upLocalPath || "", (exists: boolean) => {
        if (exists) {
          if (isFocusEnv()) {
            setStatus(FileStatus.COMPLETED);
            setPath(upLocalPath);
          } else {
            // WEB 页面 兼容
            setStatus(FileStatus.WEB_COMPLETED);
            setPath(upLocalPath);
          }
        } else {
          // setStatus(FileStatus.INIT);
          setPath("");
        }
      });
    }
  }, [message]);

  useEffect(() => {
    // console.log('文件message');
    if (message.taskId && message.task && messageUploadState) {
      const task = message.task;
      console.log("messageUploadState=>", messageUploadState, task.state);
      switch (task.state) {
        case FileStatus.COMPLETED:
          if (isFocusEnv()) {
            setStatus(FileStatus.COMPLETED);
          } else {
            // WEB 页面 兼容
            setStatus(FileStatus.WEB_COMPLETED);
          }
          setProgress(100);
          if (messageUploadState.uploadResult.downloadUrl) {
            onReady({
              beginSend: true,
              messageExtend: { url: messageUploadState.uploadResult.downloadUrl }
            });
          } else {
            // task.state = FileStatus.FAILED;
            setStatus(FileStatus.FAILED);
            setProgress(messageUploadState.progress);
            onReady({
              beginSend: false,
              messageExtend: { status: FileStatus.FAILED }
            });
          }

          break;
        case FileStatus.FAILED:
          setStatus(FileStatus.FAILED);
          setProgress(messageUploadState.progress);
          onReady({
            beginSend: false,
            messageExtend: { status: FileStatus.FAILED }
          });
          break;
        case FileStatus.CANCELED:
          setStatus(FileStatus.CANCELED);
          setProgress(messageUploadState.progress);
          break;
        case FileStatus.UPLOADING:
          setStatus(FileStatus.UPLOADING);
          setProgress(messageUploadState.progress);
          break;
      }
    } else if (message.url && !message.task) {
      if (isFocusEnv()) {
        setStatus(FileStatus.COMPLETED);
      } else {
        // WEB 页面 兼容
        setStatus(FileStatus.WEB_COMPLETED);
      }
      setProgress(0);
    }
  }, [messageUploadState, onReady]); //eslint-disable-line

  const statusConfig = useMemo(() => {
    const data: any = {
      [FileStatus.INITING]: [
        "iconapp_btn_file_cancel",
        "cancel-upload",
        "file-uploading",
        "file-success",
        "file-info-status-ing"
      ],
      [FileStatus.INIT]: [
        "iconapp_btn_file_cancel",
        "cancel-upload",
        "file-uploading",
        "",
        "file-info-status-ing"
      ],
      [FileStatus.UPLOADING]: [
        "iconapp_btn_file_cancel",
        "cancel-upload",
        "file-uploading",
        "",
        "file-info-status-ing"
      ],
      [FileStatus.CANCELED]: [
        "iconic_refresh1",
        "upload-again",
        "upload-again",
        "file-info-status-re-upload",
        "file-info-status-ing"
      ],
      [FileStatus.FAILED]: [
        "iconic_refresh1",
        "upload-again",
        // 'file-error',
        "upload-again",
        "file-info-status-re-upload",
        "file-info-status-ing"
      ],
      [FileStatus.COMPLETED]: [
        "iconbtn_localfile",
        "open-text",
        "click-to-view",
        "file-info-status-view"
      ],
      [FileStatus.WEB_COMPLETED]: [
        "",
        "click-to-save",
        "click-to-save",
        "file-info-status-view",
        "file-info-status-size"
      ],
      [FileStatus.DOWNLOADING]: [
        "iconapp_btn_file_cancel",
        "cancel-download",
        "cancel-download",
        "file-info-status-view",
        "file-info-status-ing"
      ],
      [FileStatus.DUE]: ["", "", "file-due", "file-info-status-due", ""]
    };
    return data[status];
  }, [status]);

  const netServer = NetworkCheck.getInstance({
    url: getNetCheckUrl(),
    checkType: UseCheckType.API
  });
  const groupCreate = useCallback(async () => {
    const { name } = selectedSession.info as any;
    const owner = (selectedSession as any)?.info || {};
    const onLine = await netServer.checkNet();
    if (!onLine) {
      toast.error(i18n.t("Network Error"));
      return;
    }
    let groupList = groupRosters;
    try {
      if (Array.isArray(groupList) && !groupList.length) {
        const { data, groupId } = await getGroupMemberList(
          (selectedSession as any)?.info.gid,
          "group"
        );
        groupList = data || [];
      }
      if (!groupList.length) {
        toast.error("该组已解散");
        return;
      }
      let gid = owner.gid;
      let gname = owner.name;
      let description = owner.intro;
      let admins: any = [];
      for (let item of groupList) {
        if (item.identity === "administrator" || item.identity === "sub_administrator") {
          admins.push((item as any).info.userId);
        }
      }
      let result = await groupCreates({
        admins,
        folderId: gid,
        name: gname,
        description,
        parent: "groupRoot"
      });
      if (!result || result.errMsg) {
        toast.error("create_group_team_space_error");
      }
    } catch (e) {
      e && toast.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupRosters]);

  const getFileOperateAuth = (operateTipKey: string) => {
    const authkey = getFileOperate([operateTipKey], currentFileControl || [], message.fileTag || 0);
    if (!authkey?.length) {
      return false;
    }
    return true;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps, complexity
  const fileOperate = async (operateTipKey: string, _e?: any) => {
    if (
      status !== FileStatus.COMPLETED &&
      status !== FileStatus.WEB_COMPLETED &&
      !task &&
      operateKey !== "cancel-download"
    ) {
      return false;
    }
    let config = localConfigData;
    let isExpired;
    switch (operateTipKey) {
      case "click-to-wps-preview":
        if (canWpsPreview) {
          if (status === FileStatus.CANCELED) {
            return toast.info("文件上传已取消，请重新上传");
          }
          if (!message.url || message.statusType === MessageStatus.SENDING) {
            toast.info("文件上传中，暂时无法查看");
            return;
          }
          if (message.statusType === MessageStatus.FAILED) {
            toast.info("文件发送失败，暂时无法查看");
            return;
          }
          isExpired = checkFileIsExpired({
            expiredAppKey: imAppKey,
            message,
            updateSessionMessageMid,
            expiredTime: im.fileExpiredTime
          });
          if (isExpired) return;
          if (isEgovFile) {
            toast.info(t("egov-file-preview"));
            return;
          }

          if (isWpsFile) {
            let previewParams: any = {
              url: message.url,
              mid: message.mid,
              id: message.id,
              groupId: selectedSession.isGroup ? selectedSession.sessionId : undefined
            };
            if (!isMergeForward) {
              previewParams = {
                ...previewParams,
                fileName: message.name,
                from: {
                  pin: message.from.pin,
                  app: message.from.app,
                  teamId: message.from.teamId
                }
              };
              if (selectedSession.isSingle) {
                const toUser = parseMessageToUserBySingle(selectedSession.sessionId, {
                  ...message?.from,
                  userId: message?.from?.pin
                } as any);
                previewParams.to = toUser;
              }
            } else {
              previewParams.id = (message as any).muuid;
              previewParams = {
                ...previewParams,
                from: pick(message.from || {}, ["app", "pin", "teamId"])
              };
              if (selectedSession.isSingle) {
                previewParams.to =
                  message?.to ||
                  parseMessageToUserBySingle(selectedSession.sessionId, {
                    ...message?.from,
                    userId: message?.from?.pin
                  } as any);
              }
            }
            getWpsPreviewCode(previewParams).then((res) => {
              if (res[0]) {
                openJoyspacePreview({
                  host: joyspaceHost,
                  code: res[0].code,
                  isShowHeader: 1,
                  canSaveToJoyspace: canJoyspaceEdit,
                  gid: selectedSession.isGroup ? selectedSession.sessionId : undefined,
                  gname: selectedSession.isGroup ? selectedSession.info?.name : "",
                  canEdit: canJoyspaceEdit && getPreviewCanEdit(previewParams.fileName),
                  canForward: 1
                });
              } else {
                toast.error(res[1] || "网络异常");
              }
            });
          } else if (isImageFile) {
            getPreviewImageModal({
              message,
              isMergeForward,
              modalMessages: [
                {
                  url: message.url,
                  fileName: getFileName(message),
                  muuid: message.muuid,
                  id: message.id,
                  kind: "image"
                }
              ]
            });
          } else {
            toast.info("暂不支持预览此类型文件");
          }
        } else if (!hasAuthPreview) {
          toast.info(t("file-secret-tip"));
          return;
        }
        break;
      case "click-to-save":
        if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
          toast.error(t("egov-file-notice"));
          return;
        }
        if (!isFocusEnv() && message.url) {
          // web 端下载文件
          const name = getFileName(message);
          const url = await addXclient(
            getFileUrl(message.url, getFileDownloadBzOptions(selectedSession, currentEmployee))
          );
          dXhr = downLoadFile(name, url, "", {
            onProgress: (currPercent: any) => {
              setStatus(FileStatus.DOWNLOADING);
              setProgress(currPercent);
            },
            onFinish: () => {
              setStatus(FileStatus.WEB_COMPLETED);
              setProgress(100);
              dXhr = {};
              toast.success("下载成功");
            }
          });
        }
        break;
      case "click-to-view":
        viewUploadFile({
          message,
          t,
          folderPath: "",
          updateSessionMessageMid,
          expiredTime: im.fileExpiredTime,
          currentEmployee,
          selectedSession
        });
        break;
      case "open-file":
        viewUploadFile({
          message,
          t,
          folderPath: "",
          updateSessionMessageMid,
          expiredTime: im.fileExpiredTime,
          currentEmployee,
          selectedSession
        });
        break;
      case "cancel-upload":
        setUploadStatus("canceled");
        setStatus(FileStatus.CANCELED);
        EventBus.emit(`chat:file-upload-status:${message.id}`, {
          uploadStatus: FileStatus.CANCELED
        });
        // console.log('上传', operateTipKey);
        break;
      case "cancel-download":
        dXhr.abort?.();
        setStatus(FileStatus.WEB_COMPLETED);
        setProgress(0);
        break;
      case "upload-again":
        if (status === FileStatus.FAILED) {
          task.reInit();
          return;
        }
        setUploadStatus("restart");
        setStatus(FileStatus.UPLOADING);
        EventBus.emit(`chat:file-upload-status:${message.id}`, {
          uploadStatus: FileStatus.UPLOADING
        });
        break;
      case "open-folder":
        viewUploadFile({
          message,
          t,
          folderPath: config.defaultDownloadPath || FocusSDK.getDownloadPath(),
          updateSessionMessageMid,
          expiredTime: im.fileExpiredTime,
          currentEmployee,
          selectedSession
        });
        break;
      case "click-to-saveTo":
        isExpired = checkFileIsExpired({
          expiredAppKey: imAppKey,
          message,
          updateSessionMessageMid,
          expiredTime: im.fileExpiredTime
        });
        if (isExpired) return;
        handleTransferTo(
          message,
          selectedSession,
          currentEmployee,
          groupCreate,
          appRuntimeEnv,
          userData,
          updateSessionMessageMid
        );
        break;
      case "saveTo-again":
        isExpired = checkFileIsExpired({
          expiredAppKey: imAppKey,
          message,
          updateSessionMessageMid,
          expiredTime: im.fileExpiredTime
        });
        if (isExpired) return;
        handleTransferTo(
          message,
          selectedSession,
          currentEmployee,
          groupCreate,
          appRuntimeEnv,
          userData,
          updateSessionMessageMid
        );
        break;
    }
  };

  const fileOperateClsTag = (operateTipKey: string): string => {
    if (status !== FileStatus.COMPLETED && status !== FileStatus.WEB_COMPLETED && !task) {
      return "";
    }
    switch (operateTipKey) {
      case "click-to-save":
        return "pageclick|keycount|focus_chat_01_1615797500283|35";
      case "click-to-view":
        return "pageclick|keycount|focus_chat_01_1615797500283|36";
      case "cancel-upload":
        return "pageclick|keycount|focus_chat_01_1615797500283|37";
      case "upload-again":
        return "pageclick|keycount|focus_chat_01_1615797500283|38";
      case "open-folder":
        return "pageclick|keycount|focus_chat_01_1615797500283|39";
      default:
        return "";
    }
  };

  // 文件上传进度
  const onUploadProgress = (progress: number) => {
    // setStatus(FileStatus.UPLOADING);
    // setProgress(progress);
    // console.log('onUploadComplete ==>', '文件上传进度', progress);
  };

  // 文件上传完成
  const onUploadComplete = (data: any) => {
    // console.log('onUploadComplete ==>', '文件上传完成', data, message);
    if (message.status === MessageStatus.INIT) {
      clearTempMessage({ msgId: message.beforeMid, sessionId: message.sessionId });
    }
  };
  // 图片上传失败
  const onUploadFailed = (data: any) => {
    if (message.statusType !== MessageStatus.SUCCESS) {
      setStatus(FileStatus.FAILED);
      setUploadStatus("canceled");
    }
    console.log("onUploadComplete ==>", "文件上传失败", data, message);
  };

  // 文件上传开始
  const onUploadStart = (data: any) => {
    setStatus(FileStatus.UPLOADING);
  };

  const onUploadCancel = (data: any) => {
    // console.log('onUploadCancel ==>', '文件上传取消', data);
  };

  const downloadDragFile = async () => {
    if (!FocusSDK.isPrivateNet() && message.type === ChatMessageType.EGOV_FILE) {
      toast.error(t("egov-file-notice"));
      return;
    }
    const name = getFileName(message);
    const url = await addXclient(
      getFileUrl(message.url, getFileDownloadBzOptions(selectedSession, currentEmployee))
    );
    let currDownload: DownloadItem;
    FocusSDK.downloadFile(url, {
      saveAs: false,
      filename: name,
      filter: getFileFilter(message),
      onStarted: (downloadItem: DownloadItem) => {
        currDownload = downloadItem;
      },
      onProgress: async (progress) => {
        if (progress.status === "interrupted") {
          const fileXhr = await getDownloadFileResp(url);
          if (fileXhr.status === 404 || fileXhr.response?.indexOf("1017005") > -1) {
            currDownload?.cancel();
            toast.error("当前文件已过期或已被清理");
            const imService = ImService.getInstance();
            imService.updateMessage(message.sessionId, message.mid, {
              statusType: MessageStatus.EXPIRED
            });
            if (updateSessionMessageMid) {
              updateSessionMessageMid({
                sessionId: message.sessionId,
                sendResult: { body: { ...message, statusType: MessageStatus.EXPIRED } }
              });
            }
          }
        }
      },
      onCancel: () => {},
      onPause: () => {},
      onResume: () => {},
      onFinish: async (file: { path: string; filename: string }, data: any) => {
        // 当多个文件同时下载时，file.path 获取路径错误
        const path = `${data.saveAs}${detectOS() === "Win" ? "\\" : "/"}${data.filename}`;
        // console.log('finish path', path, 'message id', message.mid);
        const imService = ImService.getInstance();
        const result = await imService.updateLocalPath(message.sessionId, message.mid, {
          upLocalPath: path
        });

        if (result) {
          setPath(path);
          setIsDragging(false);
          updateSessionMessageMid({
            sessionId: message.sessionId,
            sendResult: { body: { ...message, upLocalPath: path } }
          });
          // toast.success('下载完成');
        } else {
          toast.error(t("failed"));
          setIsDragging(false);
        }
      }
    });
  };

  const onDragStart = async (e: any) => {
    if (!isFocusEnv()) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // console.log('upload', 'message', message, 'path', path, 'isDragging', isDragging);
    if (connectState && connectState.state === ConnectState.READY) {
      if (isDragging) {
        toast.warning(t("file-downloading-drag-save"));
        return;
      }
      if (!path) {
        setIsDragging(true);
        toast.warning(t("file-downloading-drag-save"));
        downloadDragFile();
      } else {
        FocusSDK.checkLocalPathExist(path, (exists: boolean) => {
          if (exists) {
            FocusSDK.startDrag(path);
          } else {
            if (isDragging) {
              toast.warning(t("file-downloading-drag-save"));
              return;
            }
            setIsDragging(true);
            toast.warning(t("file-downloading-drag-save"));
            downloadDragFile();
          }
        });
      }
    } else {
      toast.warning(t("drag-after-the-network-is-normal"));
      return;
    }
  };

  const showFileOperate = useMemo(() => {
    if (isFocusEnv()) {
      return true;
    } else {
      return status !== FileStatus.COMPLETED;
    }
  }, [status]);

  const [operateIcon, operateTipKey, operateKey, operateClass, sizeClass] = statusConfig;
  const [fileIcon, progressColor] = getFileIconByMime(message.fileType || "");
  // console.log('上传------》》》》》', '1', status, `2`, operateTipKey, '3', message);
  const isDrag =
    FocusSDK.getDeviceInfo().platform !== "web" && !isMergeForward && !selectedSession?.isSecret;

  // 切出聊天页，组件销毁时，如果存在未完成的下载任务，自动终止下载
  useEffect(() => {
    return () => {
      dXhr.abort?.();
    };
  }, []);

  const OpenDropdownContent = useMemo(() => {
    return (
      <div className="file-dropdown-content">
        <div className="file-dropdown-item" onClick={(e) => fileOperate("open-file", e)}>
          {t("open-file")}
        </div>
        <div className="file-dropdown-item" onClick={(e) => fileOperate("open-folder", e)}>
          {t("open-folder")}
        </div>
      </div>
    );
  }, [t, fileOperate]);

  const handleClickRecentlyFile = useCallback(() => {
    const transferUrl = `${joyspaceHost}/transferResult?transferCode=${message.docId}`;
    window.open(transferUrl);
  }, [joyspaceHost, message.docId]);

  const SaveToDropdownContent = useMemo(() => {
    return (
      <div className="file-dropdown-content">
        <div className="file-dropdown-item" onClick={handleClickRecentlyFile}>
          {t("open-recently-saveTo-file")}
        </div>
        <div className="file-dropdown-item" onClick={(e) => fileOperate("saveTo-again", e)}>
          {t("saveTo-again")}
        </div>
      </div>
    );
  }, [handleClickRecentlyFile, t, fileOperate]);

  // 文件tag名称
  const fileTagName = useCallback(
    (allName = true) => {
      return getFileTagName({
        allName,
        fileTag: message?.fileTag,
        fileTagList,
        isEgovFile
      });
    },
    [message?.fileTag, fileTagList, isEgovFile]
  );
  const isIntent = useMemo(() => {
    return showIntentTail(message);
  }, [message]);

  const showFileTag = useMemo(() => {
    return isEgovFile || (message?.fileTag || 0) !== 0;
  }, [isEgovFile, message.fileTag]);

  const fileTagBottomNameComp = useMemo(() => {
    if (!showFileTag || (fileTagList?.length || 0) <= 1) {
      return null;
    }
    return FileTipTag(fileTagName(), message?.fileTag || 0);
  }, [fileTagName, message.fileTag, showFileTag]);

  return (
    <>
      <div className="content ">
        <div
          className="file"
          draggable={isDrag}
          onDragStart={onDragStart}
          style={{ width: "336px" }}
        >
          <div
            className="file-content"
            onClick={(e) => {
              fileOperate("click-to-wps-preview", e);
            }}
          >
            <div className="file-content-name">
              {message.name}
              {/* {formatFileName(message.name)} */}
            </div>
            <div className="file-content-status">
              <div className="file-content-item" style={{ alignItems: "center" }}>
                <span className={sizeClass}>{sizeToStr(message.size)}</span>
              </div>
              {isWpsFile &&
                canWpsPreview &&
                (message.statusType === MessageStatus.SUCCESS ||
                  status === FileStatus.WEB_COMPLETED) &&
                message.type !== ChatMessageType.EGOV_FILE && (
                  <div className="file-view-text">
                    <span>点击在线预览</span>
                  </div>
                )}
              {status === FileStatus.CANCELED ? (
                <div className="file-info-dow">
                  {/* 已取消 */}
                  <span className="file-sucess">已取消</span>
                </div>
              ) : (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  {status === FileStatus.FAILED && message.statusType !== MessageStatus.SUCCESS ? (
                    <div className="file-info-dow">
                      {/* 上传失败 */}
                      <span className="file-sucess">
                        {t("file-error")}
                        <IconFont
                          type="icona-ic_pc_im_reupload"
                          className="file-icon-warning"
                          clstag={fileOperateClsTag(operateKey)}
                        />
                      </span>
                    </div>
                  ) : (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                      {message.statusType === MessageStatus.SUCCESS ||
                      status === FileStatus.WEB_COMPLETED ? (
                        <div className="file-info-dow">
                          {/* 上传成功 */}
                          <span className="file-sucess">
                            {t("file-success")}
                            <IconFont
                              type="iconjs_ic_success"
                              className="file-icon-sucess"
                              clstag={fileOperateClsTag(operateKey)}
                            />
                          </span>
                        </div>
                      ) : (
                        <div className="file-info-dow">
                          {/* 正在上传 */}
                          {t(operateKey) !== "查看详情" &&
                            t(operateKey) !== "取消下载" &&
                            t(operateKey) !== "重新上传" && <span>{t(operateKey)}</span>}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="file-icon-container">
            <img
              src={changeEgovUrl(fileIcon)}
              alt=""
              className="file-icon"
              draggable="false"
              onClick={(e) => {
                fileOperate("click-to-wps-preview", e);
              }}
            />
            {/* {message.type === ChatMessageType.EGOV_FILE && (
              <img src={changeEgovUrl(egovFileIcon)} className="egov-file-icon" alt="" />
            )} */}
          </div>

          {(status === FileStatus.UPLOADING || status === FileStatus.CANCELED) && (
            <div
              className="file-img-mask"
              style={{
                backgroundImage: `conic-gradient(transparent ${
                  progress * 3.6
                }deg,  rgba(0, 0, 0, 0.3) ${progress * 3.6}deg)`
              }}
            />
          )}
          {status === FileStatus.DOWNLOADING && (
            <div
              className="file-img-mask"
              style={{
                backgroundImage: `conic-gradient(transparent ${
                  progress * 3.6
                }deg,  rgba(0, 0, 0, 0.3) ${progress * 3.6}deg)`
              }}
            />
          )}

          {message.statusType === MessageStatus.EXPIRED ? (
            <div className="file-status-bom">
              <span className="file-status-bom-text expired">{t("file-expired")}</span>
            </div>
          ) : isFocusEnv() ? (
            <div className="file-status-bom" style={{ justifyContent: "space-between" }}>
              <div className="btn-left">
                {isWpsFile &&
                  canWpsPreview &&
                  (message.statusType === MessageStatus.SUCCESS ||
                    status === FileStatus.WEB_COMPLETED) &&
                  message.type !== ChatMessageType.EGOV_FILE && (
                    <span
                      className="file-status-bom-text"
                      onClick={(e) => {
                        fileOperate("click-to-wps-preview", e);
                      }}
                    >
                      {t("wps-preview")}
                    </span>
                  )}
              </div>
              <div className="btn-right">
                {(message.statusType === MessageStatus.SUCCESS ||
                  status === FileStatus.WEB_COMPLETED) &&
                  isDisplay &&
                  !selectedSession.isSecret &&
                  (message.docId ? (
                    <Dropdown
                      overlay={SaveToDropdownContent}
                      placement="bottomCenter" /* trigger={['click']} */
                    >
                      <span
                        className="file-status-bom-text transferTo"
                        style={{ marginRight: "40px" }}
                      >
                        <span className="bom-text">{t("already-saveTo")}</span>
                        <DownOutlined />
                      </span>
                    </Dropdown>
                  ) : (
                    getFileOperateAuth("click-to-saveTo") &&
                    !isMeetingChat &&
                    message.type !== ChatMessageType.EGOV_FILE && (
                      <span
                        className="file-status-bom-text transferTo"
                        style={{ marginRight: "40px" }}
                        onClick={() => {
                          fileOperate("click-to-saveTo");
                        }}
                      >
                        {t("click-to-saveTo")}
                      </span>
                    )
                  ))}
                {operateTipKey === "open-text" ? (
                  <Dropdown
                    overlay={OpenDropdownContent}
                    placement="bottomCenter" /* trigger={['click']} */
                  >
                    <span
                      className="file-status-bom-text"
                      onClick={(e) => {
                        fileOperate(operateTipKey, e);
                      }}
                    >
                      <span className="bom-text">{t(operateTipKey)}</span>
                      <DownOutlined />
                    </span>
                  </Dropdown>
                ) : (
                  <span
                    className="file-status-bom-text"
                    onClick={(e) => {
                      fileOperate(operateTipKey, e);
                    }}
                  >
                    {t(operateTipKey)}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="file-status-bom" style={{ justifyContent: "space-between" }}>
              <div className="btn-left">
                {isWpsFile &&
                  canWpsPreview &&
                  (message.statusType === MessageStatus.SUCCESS ||
                    status === FileStatus.WEB_COMPLETED) &&
                  message.type !== ChatMessageType.EGOV_FILE && (
                    <span
                      className="file-status-bom-text"
                      onClick={(e) => {
                        fileOperate("click-to-wps-preview", e);
                      }}
                    >
                      {t("wps-preview")}
                    </span>
                  )}
              </div>
              <div className="btn-right">
                {(message.statusType === MessageStatus.SUCCESS ||
                  status === FileStatus.WEB_COMPLETED) &&
                  isDisplay &&
                  !selectedSession.isSecret &&
                  (message.docId ? (
                    <Dropdown
                      overlay={SaveToDropdownContent}
                      placement="bottomCenter" /* trigger={['click']} */
                    >
                      <span
                        className="file-status-bom-text transferTo"
                        style={{ marginRight: "40px" }}
                      >
                        <span className="bom-text">{t("already-saveTo")}</span>
                        <DownOutlined />
                      </span>
                    </Dropdown>
                  ) : (
                    !isMeetingChat &&
                    message.type !== ChatMessageType.EGOV_FILE && (
                      <span
                        className="file-status-bom-text transferTo"
                        style={{ marginRight: "40px" }}
                        onClick={() => {
                          fileOperate("click-to-saveTo");
                        }}
                      >
                        {t("click-to-saveTo")}
                      </span>
                    )
                  ))}
                <span
                  className="file-status-bom-text"
                  onClick={(e) => {
                    t(operateTipKey) === "下载"
                      ? fileOperate(operateKey, e)
                      : fileOperate(operateTipKey, e);
                  }}
                >
                  {t(operateTipKey)}
                </span>
              </div>
            </div>
          )}
        </div>
        {task?.id && (
          <UploadControl
            message={message}
            onProgress={onUploadProgress}
            onComplete={onUploadComplete}
            onFailed={onUploadFailed}
            onStart={onUploadStart}
            onCancel={onUploadCancel}
            status={uploadStatus}
          />
        )}
      </div>
      <div className="file-ai-tail">
        {isIntent ? <AIIntentTail message={message} clsName="stick" /> : null}
      </div>
      <div className={`file-extr ${showFileTag || isIntent ? "multi" : ""}`}>
        {fileTagBottomNameComp}
        {isEqualEmployee(message.sender, currentEmployee) &&
          selectedSession.isGroup &&
          (selectedSession.info as any).msgDongRead === 1 && (
            <MessageDongReadStatusBtn {...props} />
          )}
        {isEqualEmployee(message.sender, currentEmployee) && !selectedSession.isSecret && (
          <div className="upload-detail-btn-box">
            <UploadWithDetailBtn {...props} />
          </div>
        )}
      </div>
    </>
  );
}

function mapStateToProps({ chat, user: { userData } }: any) {
  const {
    messageUploadStateMap,
    connectState,
    currentEmployee,
    selectedSession
  } = chat as ChatState;
  return {
    messageUploadStateMap,
    connectState,
    currentEmployee,
    selectedSession,
    userData: userData ? userData.user : {}
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { message: ChatMessage; sessionId: string }) {
      dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    },
    clearTempMessage(data: { msgId: string }) {
      dispatch({ type: "chat/clearTempMessage", payload: data });
    },
    pushChatMessageMid(data: { sessionId: string; message: Partial<ChatMessage> }) {
      dispatch({ type: "chat/pushChatMessageMid", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadFile);
